import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Flex, Space } from 'antd';
import { showAlertNotification } from 'components/common';
import BarcodeCommon from 'components/common/Barcode/BarcodeCommon';
import { ButtonCommon } from 'components/common/Button/ButtonCommon';
import { InputCommon } from 'components/common/Input/InputCommon';
import PrefixedInput from 'components/common/Input/PrefixedInput';
import LabelWithRequired from 'components/common/Label/LabelWithRequired';
import SelectCommon from 'components/common/Select/SelectCommon';
import {
  useCreateProductMutation,
  useGetAllCategoriesQuery,
  useGetAllBrandsQuery,
  useGetAllUnitsQuery,
  useGenerateCodeMutation,
  useGetStatusQuery,
} from 'services';
import { generateRandomBarcode } from 'utils';
import { ShoppingCart } from '@phosphor-icons/react';
import { ProductPayload } from 'types';

interface QuickAddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onAddToCart?: (product: ProductPayload, unitId: string) => void;
}

const QuickAddProductModal: React.FC<QuickAddProductModalProps> = ({ isOpen, onClose, onSuccess, onAddToCart }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');

  const [createProduct] = useCreateProductMutation();
  const [generateCode] = useGenerateCodeMutation();
  const { data: categoriesData } = useGetAllCategoriesQuery({
    query: { size: 'unlimited', filter: 'isActive:eq:true' },
  });
  const { data: brandsData } = useGetAllBrandsQuery({ query: { size: 'unlimited', filter: 'isActive:eq:true' } });
  const { data: unitsData } = useGetAllUnitsQuery({ query: { size: 'unlimited' } });
  const { data: statusData } = useGetStatusQuery({ query: { filter: 'code:eq:pr-active' } });

  const unitOptions =
    unitsData?.data.rows
      ?.filter((unit) => unit.type === 'unit')
      .map((unit) => ({
        label: unit.name,
        value: unit.id,
      })) || [];

  const currencyUnits =
    unitsData?.data.rows
      ?.filter((unit) => unit.type === 'currency')
      .map((unit) => ({
        label: unit.name,
        value: unit.id,
      })) || [];

  // Initialize form with generated code and barcode on modal open
  useEffect(() => {
    if (isOpen) {
      initializeForm();
    }
  }, [isOpen]);

  // Set default currency to VND when list loaded
  useEffect(() => {
    const vndValue = currencyUnits.find((u) => u.label === 'VNĐ')?.value;
    if (vndValue) {
      form.setFieldsValue({ currencyUnitId: vndValue });
    }
  }, [currencyUnits, form]);

  const initializeForm = async () => {
    try {
      const codeResponse = await generateCode({ code: 'SP' }).unwrap();
      const newCode = codeResponse || `SP${Date.now()}`;
      const newBarcode = generateRandomBarcode();

      setGeneratedCode(newCode);
      setBarcode(newBarcode);
      form.setFieldsValue({
        code: newCode,
        barcode: newBarcode,
      });
    } catch (error) {
      console.error('Error generating code/barcode:', error);
      const newCode = `SP${Date.now()}`;
      const newBarcode = generateRandomBarcode();
      setGeneratedCode(newCode);
      setBarcode(newBarcode);
      form.setFieldsValue({
        code: newCode,
        barcode: newBarcode,
      });
    }
  };

  const handleCreateCode = async () => {
    try {
      const res = await generateCode({ code: 'SP' }).unwrap();
      if (res) {
        setGeneratedCode(res);
        form.setFieldsValue({ code: res });
      } else {
        console.error('Code generation failed, no data returned.');
      }
    } catch (error) {
      console.error('Error generating code:', error);
    }
  };

  const handleBarcode = () => {
    const newBarcode = generateRandomBarcode();
    setBarcode(newBarcode);
    form.setFieldsValue({ barcode: newBarcode });
  };

  const handleSubmit = async (addToCart = false) => {
    try {
      const values = await form.validateFields();
      const errors = [];
      if (!values.name || values.name.trim() === '') {
        errors.push({
          name: 'name',
          errors: ['Vui lòng nhập tên sản phẩm'],
        });
      }
      // Validate required fields and collect errors
      if (!values.code || values.code.trim() === '') {
        errors.push({
          name: 'code',
          errors: ['Vui lòng nhập mã sản phẩm'],
        });
      }

      if (!values.categoryId) {
        errors.push({
          name: 'categoryId',
          errors: ['Vui lòng chọn danh mục'],
        });
      }

      if (!values.brandId) {
        errors.push({
          name: 'brandId',
          errors: ['Vui lòng chọn thương hiệu'],
        });
      }

      if (!values.baseUnitId) {
        errors.push({
          name: 'baseUnitId',
          errors: ['Vui lòng chọn đơn vị cơ bản'],
        });
      }

      if (errors.length > 0) {
        form.setFields(errors);
        return;
      }

      setLoading(true);

      // Get the pr-active status ID
      const statusId = statusData?.data?.[0]?.id || '69ea282b-b7f4-4e26-85fa-a384a63a976d';
      const vndCurrencyId =
        values.currencyUnitId || currencyUnits.find((u) => u.label === 'VNĐ')?.value || currencyUnits[0]?.value;

      if (!vndCurrencyId) {
        showAlertNotification({
          type: 'error',
          title: 'Thiếu thông tin',
          message: 'Không tìm thấy đơn vị tiền tệ mặc định. Vui lòng chọn lại.',
        });
        setLoading(false);
        return;
      }

      const payload = {
        name: values.name,
        code: values.code || generatedCode,
        barcode: values.barcode || barcode,
        categoryId: values.categoryId || undefined,
        brandId: values.brandId || undefined,
        baseUnitId: values.baseUnitId || undefined,
        currencyUnitId: vndCurrencyId,
        description: values.description || '',
        minQuantity: 0,
        maxQuantity: 9999,
        costPrice: Number(values.costPrice) || 0,
        salePrice: Number(values.salePrice) || 0,
        isPublishing: false,
        statusId: statusId,
        quantity: Number(values.quantity ?? 0),
        // productVersions: [
        //   {
        //     name: values.name,
        //     salePrice: Number(values.salePrice) || 0,
        //     costPrice: Number(values.costPrice) || 0,
        //     quantity: Number(values.quantity ?? 0),
        //     isDefault: true,
        //   },
        // ],
      };

      const response = await createProduct(payload as ProductPayload).unwrap();

      if (response.success) {
        showAlertNotification({
          type: 'success',
          title: 'Thành công',
          message: `Đã thêm sản phẩm "${values.name}" thành công.`,
        });
        form.resetFields();
        setBarcode('');
        setGeneratedCode('');
        if (addToCart && response.data) {
          // Merge API response with form values to ensure complete data
          const completeProduct = {
            ...response.data,
            categoryId: values.categoryId || response.data.categoryId,
            brandId: values.brandId || response.data.brandId,
            baseUnitId: values.baseUnitId || response.data.baseUnitId,
            category: categoriesData?.data.rows?.find((c) => c.id === values.categoryId),
            brand: brandsData?.data.rows?.find((b) => b.id === values.brandId),
            baseUnit: unitsData?.data.rows?.find((u) => u.id === values.baseUnitId),
          };
          onAddToCart?.(completeProduct, values.baseUnitId);
        } else {
          onSuccess?.();
        }
        onClose();
      }
    } catch (error: unknown) {
      console.error('Error creating product:', error);
      const errorMessage =
        (error as { data?: { message?: { message?: string[] } } })?.data?.message?.message?.[0] ||
        'Không thể thêm sản phẩm. Vui lòng thử lại.';
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setBarcode('');
    setGeneratedCode('');
    onClose();
  };

  const codePrefix = (form.getFieldValue('code') || generatedCode || 'SP')?.substring(0, 6) || 'SP';

  return (
    <Modal
      title={<div className='text-lg font-semibold p-4'>Thông tin sản phẩm</div>}
      open={isOpen}
      onCancel={handleCancel}
      footer={
        <Space size='middle' className='gap-2 lg:gap-3 flex justify-end mr-5' align='center'>
          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-white px-6 text-black'
            onClick={handleCancel}
          >
            Bỏ qua
          </ButtonCommon>

          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-white px-6 text-black'
            loading={loading}
            onClick={() => handleSubmit(false)}
          >
            <span className='whitespace-nowrap'>Lưu sản phẩm</span>
          </ButtonCommon>
          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-[var(--primaryColor500)] px-3 gap-2'
            loading={loading}
            onClick={() => handleSubmit(true)}
          >
            <div className='w-5 h-5'>
              <ShoppingCart size={20} />
            </div>
            <span className='whitespace-nowrap text-white'>Lưu và thêm vào giỏ hàng</span>
          </ButtonCommon>
        </Space>
      }
      width={1120}
      centered
      bodyStyle={{ padding: '20px' }}
    >
      <Form form={form} layout='vertical'>
        <Flex vertical gap={16}>
          {/* Product Name and Barcode Row */}
          <Flex justify='space-between' align='center' gap={24}>
            <Form.Item
              label={<LabelWithRequired label='Tên hàng' />}
              name='name'
              required={false}
              className='mb-0 flex-1'
            >
              <InputCommon type='label' placeholder='Nhập tên sản phẩm...' maxLength={100} />
            </Form.Item>
            <Form.Item label={<LabelWithRequired label='Barcode' />} name='barcode' className='mb-0'>
              <BarcodeCommon
                valueBarcode={barcode || form.getFieldValue('barcode')}
                onGenerate={handleBarcode}
                isDisabled={false}
              />
            </Form.Item>
          </Flex>

          {/* Row 1: Code, Giá vốn, Giá bán */}
          <Row gutter={[24, 12]}>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Mã' />} name='code' className='mb-0'>
                <PrefixedInput
                  prefix={codePrefix || ''}
                  placeholder='Nhập 5 số'
                  // limit editable part to 5 characters
                  maxEditableLength={5}
                  // keep PrefixedInput in sync with form and generatedCode
                  value={form.getFieldValue('code') || generatedCode}
                  onChange={(val: string) => form.setFieldsValue({ code: val })}
                  disabled={false}
                  inputProps={{
                    suffix: (
                      <ButtonCommon
                        color='barcode'
                        fullWidth
                        radius='rounded'
                        size='xs'
                        onClick={handleCreateCode}
                        isDisabled={false}
                      >
                        Generate
                      </ButtonCommon>
                    ),
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                className='mb-0'
                label={<span className='font-medium text-black'>Giá vốn</span>}
                name='costPrice'
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const raw = String(value).replace(/\D/g, '');
                      if (raw.length > 9) {
                        return Promise.reject('Giá vốn không được vượt quá 9 chữ số');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputCommon type='money' placeholder='Nhập giá vốn' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<span className='font-medium text-black'>Giá bán</span>}
                name='salePrice'
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const raw = String(value).replace(/\D/g, '');
                      if (raw.length > 9) {
                        return Promise.reject('Giá bán không được vượt quá 9 chữ số');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className='mb-0'
              >
                <InputCommon type='money' placeholder='Nhập giá bán' />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 12]}>
            <Col span={8}>
              <Form.Item label={<span className='font-medium text-black'>Tồn</span>} name='quantity' className='mb-0'>
                <InputCommon type='number' placeholder='Nhập tồn kho' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Danh mục, Thương hiệu, Đơn vị cơ bản */}
          <Row gutter={[24, 12]}>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Danh mục' />} name='categoryId' className='mb-0'>
                <SelectCommon
                  placeholderSelect='Chọn danh mục'
                  options={
                    categoriesData?.data.rows
                      ?.filter((cat) => cat.isActive)
                      .map((cat) => ({
                        label: cat.name,
                        value: cat.id,
                      })) || []
                  }
                  selectType='filterSelect'
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Thương hiệu' />} name='brandId' className='mb-0'>
                <SelectCommon
                  placeholderSelect='Chọn thương hiệu'
                  options={
                    brandsData?.data.rows
                      ?.filter((brand) => brand.isActive)
                      .map((brand) => ({
                        label: brand.name,
                        value: brand.id,
                      })) || []
                  }
                  selectType='filterSelect'
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Đơn vị cơ bản' />} name='baseUnitId' className='mb-0'>
                <SelectCommon
                  placeholderSelect='Chọn đơn vị'
                  options={unitOptions}
                  selectType='filterSelect'
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Flex>
      </Form>
    </Modal>
  );
};

export default QuickAddProductModal;
